import { MOSIDA_API_BASE_URL } from './mosidaApi';
import { getColorLabel } from './shopUtils';
import { toCdnMediaUrl } from '../utils/cdnMediaUrl';

/** Alias for merch / pack color matching (Giftshop `getProductColorLabel`). */
export const getProductColorLabel = getColorLabel;

function isUsableProductImage(value) {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  const lower = trimmed.toLowerCase();
  return lower !== 'null' && lower !== 'undefined' && lower !== '[object object]';
}

export function normalizeMediaUrl(value, _baseHint) {
  if (!isUsableProductImage(value)) return undefined;
  const trimmed = value.trim();
  if (/^(data:|blob:)/i.test(trimmed)) return trimmed;
  if (trimmed.startsWith('//')) {
    return toCdnMediaUrl(`https:${trimmed}`.replace(/ /g, '%20'));
  }
  // Absolute S3 / relative object keys → public CDN.
  return toCdnMediaUrl(trimmed.replace(/ /g, '%20'));
}

function firstNormalizedImage(candidates, baseHint) {
  for (const candidate of candidates) {
    const normalized = normalizeMediaUrl(
      typeof candidate === 'string' ? candidate : undefined,
      baseHint
    );
    if (normalized) return normalized;
  }
  return undefined;
}

export function resolveProductDisplayImage(product) {
  const hint = [
    product?.picture,
    product?.image,
    product?.imgURL,
    product?.image_url,
  ].find(
    (value) => isUsableProductImage(value) && /^https?:\/\//i.test(value.trim())
  );
  const colorImages = Array.isArray(product?.color)
    ? product.color.flatMap((color) => {
        if (!color || typeof color !== 'object') return [];
        return [
          color.productImageUrl,
          color.product_image_url,
          color.picture,
          color.image,
          color.imgURL,
          color.imageUrl,
          color.image_url,
        ];
      })
    : [];

  return firstNormalizedImage(
    [
      product?.picture,
      product?.image,
      product?.imgURL,
      product?.image_url,
      ...colorImages,
    ],
    hint
  );
}

export function productName(product) {
  if (typeof product?.name === 'string') return product.name;
  if (product?.name && typeof product.name === 'object') {
    return product.name.name || 'Product';
  }
  return 'Product';
}

export async function fetchAllProducts() {
  try {
    const response = await fetch(`${MOSIDA_API_BASE_URL}/product/all`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      const errorText = await response.text();
      return {
        success: false,
        error: `Failed to fetch products: ${response.status} ${errorText}`,
        data: [],
      };
    }
    const data = await response.json();
    const products = data?.data || data?.products || data || [];
    return {
      success: true,
      data: Array.isArray(products) ? products : [],
    };
  } catch (error) {
    return {
      success: false,
      error: error?.message || 'Network error while fetching products',
      data: [],
    };
  }
}
